import { useEffect, useState } from "react";
import { ChangeLog } from "../models/ChangeLog/ChangeLog";
import agent from "../../api/agent";
import {
  Typography,
  TableContainer,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Box,
  TablePagination,
  Divider,
  Stack,
  CircularProgress,
  Tooltip,
  Fab,
  FormControl,
  Select,
  Autocomplete,
  Chip,
  InputLabel,
  MenuItem,
  TextField,
  Button,
  DialogActions,
  Dialog,
  DialogContent,
  RadioGroup,
  Radio,
  FormControlLabel,
  IconButton,
  DialogTitle,
} from "@mui/material";
import LoadingComponent from "../layout/share_components/LoadingComponent";
import { checkTokenExpiration } from "../../api/tokenExpirationChecking";
import GetAppIcon from "@mui/icons-material/GetApp";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import CloseIcon from "@mui/icons-material/Close";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { showToast } from "../layout/share_components/ToastMessage";
import LogDetails from "../details/LogDetails";

export default function LogDashboard() {
  const [loading, setLoading] = useState<boolean>(true);
  const [logList, setLogList] = useState<ChangeLog[]>([]);
  const [filteredLogList, setFilteredLogList] = useState<ChangeLog[]>([]);

  // Pagination (Change Log)
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);

  // filteration
  const [selectedValues, setSelectedValues] = useState<string | null>(null);
  const [searchColumn, setSearchColumn] = useState<
    "tableName" | "action" | "emailChangeBy"
  >("tableName");

  // Authentication
  const [token, setToken] = useState<string>("");

  // For export to CSV
  const [exportLoading, setExportLoading] = useState<boolean>(false);

  // For role filteration
  const [roleFilter, setRoleFilter] = useState<string>("Super Admin");
  const [roleFilterDialogOpenState, setRoleFilterDialogOpenState] =
    useState<boolean>(false);

  // For showing the remark text
  const [remarkDialogOpenState, setRemarkDialogOpenState] =
    useState<boolean>(false);
  const [remarkContent, setRemarkContent] = useState<string>("");

  useEffect(() => {
    const fetchData = async () => {
      try {
        // get the token from localstorage
        const tokenFLocal = localStorage.getItem("token");
        if (tokenFLocal !== null) {
          setToken(tokenFLocal);
          const logResponse = await agent.ChangeLog.list(tokenFLocal);
          setLogList(logResponse);
          setFilteredLogList(logResponse);
          setLoading(false);
        }
      } catch (error) {
        console.error("Error fetching data:", error);
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  // Function for the search and filter
  // Get current options based on selected column
  const getCurrentOptions = () => {
    switch (searchColumn) {
      case "tableName":
        return Array.from(
          new Set(filteredLogList.map((log) => log.tableName).filter(Boolean)),
        );
      case "action":
        return Array.from(
          new Set(filteredLogList.map((log) => log.action).filter(Boolean)),
        );
      case "emailChangeBy":
        return Array.from(
          new Set(
            filteredLogList.map((log) => log.emailChangeBy).filter(Boolean),
          ),
        );
      default:
        return [];
    }
  };

  const handleColumnChange = (event: any) => {
    setSearchColumn(event.target.value);
    setSelectedValues(null);
  };

  const handleValueChange = (event: any, newValues: string | null) => {
    setSelectedValues(newValues);
  };

  function applyFilters() {
    let filtered = [...logList];

    // 1. Apply main search column filter (if any)
    if (selectedValues) {
      filtered = filtered.filter((log) => {
        const columnValue = String(
          log[searchColumn as keyof ChangeLog],
        ).toLowerCase();
        return columnValue.includes(selectedValues.toLowerCase());
      });
    }

    // 2. Apply role filter (always applied based on current selection)
    if (roleFilter === "Super Admin") {
      filtered = filtered.filter((log) => log.role === "Normal");
    } else if (roleFilter === "Developer") {
      filtered = filtered.filter((log) => log.role === "Developer");
    }

    setFilteredLogList(filtered);
  }

  useEffect(() => {
    if (logList.length > 0) {
      applyFilters();
    }
  }, [selectedValues, searchColumn, logList, roleFilter]);

  // Function for role filter dialog
  const handleRoleFilterDialogOpen = () => {
    setRoleFilterDialogOpenState(true);
  };

  const handleRoleFilterDialogClose = () => {
    setRoleFilterDialogOpenState(false);
  };

  // Function for remark dialog
  const handleRemarkDialogOpen = (remark: string) => {
    setRemarkContent(remark);
    setRemarkDialogOpenState(true);
  };

  const handleRemarkDialogClose = () => {
    setRemarkDialogOpenState(false);
    setRemarkContent("");
  };

  // Function for pagination
  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  // Handle rows per page change
  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Function to export software list to CSV file
  const handleExport = async () => {
    if (checkTokenExpiration(token)) {
      return;
    }
    setExportLoading(true);
    try {
      const response = await agent.ChangeLog.exportToCSV(token);
      const blob = new Blob([response.data], { type: "text/csv" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      let filename = `change-log-export-${new Date().toISOString().slice(0, 19).replace(/:/g, "-")}.csv`;
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      showToast("Exported to CSV successfully", "success");
    } catch (error) {
      console.error("Error exporting to CSV:", error);
      showToast("Error exporting to CSV", "error");
    } finally {
      setExportLoading(false);
    }
  };

  // Reuseable pagination component
  const PaginationComponent = () => (
    <TablePagination
      rowsPerPageOptions={[10, 25, 50, 100]} // Options for rows per page
      component="div"
      count={filteredLogList.length} // Total number of rows
      rowsPerPage={rowsPerPage}
      page={page}
      onPageChange={handleChangePage}
      onRowsPerPageChange={handleChangeRowsPerPage}
      sx={{
        ".MuiTablePagination-toolbar": {
          paddingLeft: 2, // Adjust padding as needed
        },
      }}
    />
  );

  // Trigger the loader when fetching data from database
  if (loading) return <LoadingComponent content="Loading Data..." />;

  return (
    <>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{ mb: 2 }}
      >
        <Typography variant="h5">Change Logs</Typography>
        <Stack direction="row" spacing={1}>
          <Tooltip title="Filter By Role" followCursor>
            <Fab
              color="primary"
              onClick={() => handleRoleFilterDialogOpen()}
              size="small"
            >
              <FilterAltIcon />
            </Fab>
          </Tooltip>
          <Tooltip title="Export to CSV" followCursor>
            <span>
              <Fab
                color="success"
                onClick={() => handleExport()}
                size="small"
                disabled={exportLoading}
              >
                {exportLoading ? (
                  <CircularProgress size={24} />
                ) : (
                  <GetAppIcon />
                )}
              </Fab>
            </span>
          </Tooltip>
        </Stack>
      </Stack>

      <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
        <Autocomplete
          options={getCurrentOptions()}
          value={selectedValues}
          onChange={handleValueChange}
          renderInput={(params) => (
            <TextField
              {...params}
              variant="standard"
              label="Filter"
              placeholder="Choose to filter"
            />
          )}
          renderTags={(value: string[], getTagProps) =>
            value.map((option: string, index: number) => (
              <Chip label={option} {...getTagProps({ index })} size="small" />
            ))
          }
          sx={{ minWidth: 300 }}
        />

        {/* For letting user to choose which column to be filter */}
        <FormControl sx={{ ml: 1 }}>
          <InputLabel>Filter by</InputLabel>
          <Select
            value={searchColumn}
            label="Filter by"
            onChange={handleColumnChange}
          >
            <MenuItem value="tableName">Table Name</MenuItem>
            <MenuItem value="action">Action</MenuItem>
            <MenuItem value="emailChangeBy">Email</MenuItem>
          </Select>
        </FormControl>
        <Box sx={{ flex: 1 }} />
        <PaginationComponent />
      </Box>
      {filteredLogList.length === 0 ? (
        <Box sx={{ mt: 5 }}>
          <Divider>
            <Typography variant="button">No Record found</Typography>
          </Divider>
        </Box>
      ) : (
        <>
          <TableContainer component={Paper} sx={{ mt: 2 }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Table Name</TableCell>
                  <TableCell>Action</TableCell>
                  <TableCell>Changed By</TableCell>
                  <TableCell>Changed On</TableCell>
                  <TableCell>Details</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredLogList
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((log) => (
                    <>
                      <TableRow key={log.changeLogId}>
                        <TableCell>{log.tableName}</TableCell>
                        <TableCell>{log.action}</TableCell>
                        <TableCell>{log.emailChangeBy}</TableCell>
                        <TableCell>
                          {/* Auto-converts to user's local timezone */}
                          {new Date(log.changedOn + "Z").toLocaleString()}
                        </TableCell>
                        <TableCell>
                          {log.remark ? (
                            <Tooltip title="View Remark/Details">
                              <IconButton
                                size="small"
                                onClick={() =>
                                  handleRemarkDialogOpen(log.remark!)
                                }
                              >
                                <VisibilityIcon />
                              </IconButton>
                            </Tooltip>
                          ) : (
                            <Tooltip title="No Remark/Details available">
                              <IconButton
                                size="small"
                                sx={{
                                  opacity: 0.5,
                                }}
                              >
                                <VisibilityIcon />
                              </IconButton>
                            </Tooltip>
                          )}
                        </TableCell>
                      </TableRow>
                    </>
                  ))}
              </TableBody>
            </Table>
            {/* Pagination Component (Company Table) */}
            <TablePagination
              rowsPerPageOptions={[10, 25, 50, 100]} // Options for rows per page
              component="div"
              count={filteredLogList.length} // Total number of rows
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              sx={{
                ".MuiTablePagination-toolbar": {
                  paddingLeft: 2, // Adjust padding as needed
                },
              }}
            />
          </TableContainer>
          {/* Dialog for remark details */}
          <Dialog
            open={remarkDialogOpenState}
            onClose={handleRemarkDialogClose}
            maxWidth="sm"
            fullWidth
            slotProps={{
              backdrop: { sx: { bgcolor: "rgba(0, 0, 0, 0.5)" } },
              paper: {
                sx: {
                  width: { xs: "90%", sm: "80%", md: "70%" }, // Responsive width
                  maxWidth: "960px", // Matches 'md' breakpoint
                  maxHeight: "80vh", // Limit height
                  overflowY: "auto", // Scroll if needed
                  borderRadius: 2,
                },
              },
            }}
          >
            <DialogTitle>
              <Typography variant="h6" component="h2">
                Remark Details
              </Typography>
            </DialogTitle>
            <DialogContent>
              {remarkContent && (
                <LogDetails
                  remark={remarkContent}
                  closeForm={handleRemarkDialogClose}
                />
              )}
            </DialogContent>
          </Dialog>
        </>
      )}

      {/* Dialog for role filter */}
      <Dialog
        open={roleFilterDialogOpenState}
        onClose={handleRoleFilterDialogClose}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            p: 1,
          },
        }}
      >
        <DialogTitle
          sx={{
            m: 0,
            p: 2,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography variant="h6">Filter by Role</Typography>
          <IconButton
            aria-label="close"
            onClick={handleRoleFilterDialogClose}
            sx={{ color: "grey.500" }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers>
          <FormControl component="fieldset" sx={{ width: "100%" }}>
            <RadioGroup
              value={roleFilter}
              onChange={(e) => {
                setRoleFilter(e.target.value);
                setRoleFilterDialogOpenState(false);
              }}
            >
              <FormControlLabel
                value="Super Admin"
                control={<Radio />}
                label={
                  <Box>
                    <Typography variant="body1">Super Admin</Typography>
                    <Typography variant="caption" color="textSecondary">
                      Show only Super Admin activities
                    </Typography>
                  </Box>
                }
                sx={{ mb: 2 }}
              />

              <FormControlLabel
                value="Developer"
                control={<Radio />}
                label={
                  <Box>
                    <Typography variant="body1">Developer</Typography>
                    <Typography variant="caption" color="textSecondary">
                      Show only Developer activities
                    </Typography>
                  </Box>
                }
                sx={{ mb: 2 }}
              />

              <FormControlLabel
                value="Show All"
                control={<Radio />}
                label={
                  <Box>
                    <Typography variant="body1">Show All</Typography>
                    <Typography variant="caption" color="textSecondary">
                      Display all activities regardless of role
                    </Typography>
                  </Box>
                }
              />
            </RadioGroup>
          </FormControl>
        </DialogContent>

        <DialogActions sx={{ p: 2 }}>
          <Box
            sx={{
              display: "flex",
              gap: 2,
              width: "100%",
              justifyContent: "flex-end",
            }}
          >
            <Button variant="outlined" onClick={handleRoleFilterDialogClose}>
              Close
            </Button>
          </Box>
        </DialogActions>
      </Dialog>
    </>
  );
}
